import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useQuery,
  getUserArticleFlags,
  setArticleFlag,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { routes } from "wasp/client/router";

interface ArticleFlagContextType {
  flaggedArticleIds: number[];
  toggleFlag: (articleId: number, reason: string) => Promise<void>;
  isFlagged: (articleId: number) => boolean;
  isFlagPending: (articleId: number) => boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const ArticleFlagContext = createContext<ArticleFlagContextType | undefined>(
  undefined,
);

export function ArticleFlagProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: user, isLoading: authLoading } = useAuth();
  const { data: flaggedIds, isLoading: flagsLoading } = useQuery(
    getUserArticleFlags,
    undefined,
    { enabled: !!user },
  );
  const [flaggedSet, setFlaggedSet] = useState<Set<number>>(new Set());
  const [pendingFlagIds, setPendingFlagIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!user) {
      setFlaggedSet(new Set());
      setPendingFlagIds(new Set());
      return;
    }

    setFlaggedSet(new Set(flaggedIds ?? []));
  }, [flaggedIds, user]);

  const toggleFlag = useCallback(
    async (articleId: number, reason: string) => {
      if (!user) {
        window.location.href = routes.LoginRoute.to;
        return;
      }

      if (pendingFlagIds.has(articleId)) return;

      const currentlyFlagged = flaggedSet.has(articleId);

      setPendingFlagIds((prev) => {
        const next = new Set(prev);
        next.add(articleId);
        return next;
      });

      setFlaggedSet((prev) => {
        const next = new Set(prev);
        if (currentlyFlagged) {
          next.delete(articleId);
        } else {
          next.add(articleId);
        }
        return next;
      });

      try {
        await setArticleFlag({ articleId, reason });
      } catch (error) {
        setFlaggedSet((prev) => {
          const next = new Set(prev);
          if (currentlyFlagged) {
            next.add(articleId);
          } else {
            next.delete(articleId);
          }
          return next;
        });
        throw error;
      } finally {
        setPendingFlagIds((prev) => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
      }
    },
    [flaggedSet, pendingFlagIds, user],
  );

  const value = useMemo(
    () => ({
      flaggedArticleIds: Array.from(flaggedSet),
      toggleFlag,
      isFlagged: (articleId: number) => flaggedSet.has(articleId),
      isFlagPending: (articleId: number) => pendingFlagIds.has(articleId),
      isAuthenticated: !!user,
      isLoading: authLoading || flagsLoading,
    }),
    [authLoading, flaggedSet, flagsLoading, pendingFlagIds, toggleFlag, user],
  );

  return (
    <ArticleFlagContext.Provider value={value}>
      {children}
    </ArticleFlagContext.Provider>
  );
}

export function useArticleFlags() {
  const context = useContext(ArticleFlagContext);
  if (!context) {
    throw new Error(
      "useArticleFlags must be used within an ArticleFlagProvider",
    );
  }
  return context;
}
